import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { DataTable, type Column, type SortDirection } from "../ui/data-table";
import { PlatformBadge } from "./PlatformBadge";
import { keyEventsInfo, landingPageInfo, platformInfo, sessionsInfo } from "./metric-info";
import { downloadXlsx } from "../../lib/xlsx";
import { toCsv } from "../../../shared/csv";
import type { ReportLandingPageRow } from "../../lib/api";
import type { CanonicalPlatform } from "../../../shared/utm/types";

interface LandingPageTableProps {
  rows: ReportLandingPageRow[];
  propertyLabel: string;
  periodLabel: string;
  /** Threaded to the Conversions column tooltip for the GA4 deep-link. */
  propertyId?: string;
}

type PageSize = 5 | 15 | 25 | 50 | 100 | "all";
const PAGE_SIZES: PageSize[] = [5, 15, 25, 50, 100, "all"];
const DEFAULT_PAGE_SIZE: PageSize = 15;
const MAX_ROWS = 250;
type PlatformFilter = "all" | CanonicalPlatform;

export function LandingPageTable({
  rows,
  propertyLabel,
  periodLabel,
  propertyId,
}: LandingPageTableProps) {
  const [sortColumn, setSortColumn] = useState<string>("sessions");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>("all");

  const columns = useMemo<Column<ReportLandingPageRow>[]>(
    () => [
      {
        id: "landingPage",
        header: "Landing page",
        sortable: true,
        accessorKey: "landingPage",
        info: landingPageInfo,
        cell: (row) => <LandingPageCell row={row} />,
      },
      {
        id: "platform",
        header: "Top platform",
        sortable: true,
        accessorKey: "platform",
        info: platformInfo,
        cell: (row) => <PlatformBadge platform={row.platform} />,
      },
      {
        id: "sessions",
        header: "Sessions",
        sortable: true,
        accessorKey: "sessions",
        info: sessionsInfo,
        cell: (row) => <span className="tabular-nums">{row.sessions.toLocaleString()}</span>,
      },
      {
        id: "keyEvents",
        header: "Conversions",
        sortable: true,
        accessorKey: "keyEvents",
        info: keyEventsInfo(propertyId),
        cell: (row) => <span className="tabular-nums">{row.keyEvents.toLocaleString()}</span>,
      },
    ],
    [propertyId],
  );

  // Top-250 window drives both the visible table and the CSV export so the
  // CSV matches what the user has been browsing.
  const cappedRows = useMemo(() => rows.slice(0, MAX_ROWS), [rows]);

  // Set of platforms actually present in the data, ordered by session
  // volume — drives the filter dropdown so we don't offer platforms the
  // property doesn't see.
  const availablePlatforms = useMemo(() => {
    const totals = new Map<CanonicalPlatform, number>();
    for (const row of cappedRows) {
      totals.set(row.platform, (totals.get(row.platform) ?? 0) + row.sessions);
    }
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([platform]) => platform);
  }, [cappedRows]);

  // Filter (search + platform) applied BEFORE sort + slice so the counter,
  // pagination math, and CSV export all line up with what's actually visible.
  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return cappedRows.filter((row) => {
      if (platformFilter !== "all" && row.platform !== platformFilter) return false;
      if (!needle) return true;
      return row.landingPage.toLowerCase().includes(needle);
    });
  }, [cappedRows, platformFilter, search]);

  // Parent owns sort state so pagination sorts the full filtered set rather
  // than just the current page. The `key` on <DataTable> forces remount when
  // sort changes so its internal sort picks up the new defaults.
  const sortedRows = useMemo(() => {
    if (!sortDirection) return filteredRows;
    return [...filteredRows].sort((a, b) => comparator(a, b, sortColumn, sortDirection));
  }, [filteredRows, sortColumn, sortDirection]);

  const totalRows = sortedRows.length;
  const effectivePageSize = pageSize === "all" ? totalRows : pageSize;
  const pageCount = Math.max(1, Math.ceil(totalRows / (effectivePageSize || 1)));
  const safePage = Math.min(currentPage, pageCount - 1);
  const sliceStart = effectivePageSize ? safePage * effectivePageSize : 0;
  const sliceEnd = effectivePageSize ? sliceStart + effectivePageSize : totalRows;
  const pagedRows = useMemo(
    () => sortedRows.slice(sliceStart, sliceEnd),
    [sortedRows, sliceStart, sliceEnd],
  );

  if (rows.length === 0) {
    return (
      <div className="rounded-xl bg-white p-8 text-center ring-1 ring-neutral-950/5">
        <p className="font-display text-base font-semibold text-neutral-950">
          No landing pages this period
        </p>
        <p className="mt-2 text-sm text-neutral-600 text-pretty">
          Once social traffic lands somewhere on your site, the top entry pages will appear here.
        </p>
      </div>
    );
  }

  // Export mirrors what the user is seeing — the current filter + search
  // applies so they can export a slice without loading "all" first.
  const exportSlug = propertyLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const exportFilenameBase = `landing-pages-${exportSlug || "report"}-${new Date()
    .toISOString()
    .slice(0, 10)}`;
  const exportHeaders = ["landing_page", "page_url", "top_platform", "sessions", "conversions"];
  const exportRows = () =>
    filteredRows.map((r) => [r.landingPage, r.pageUrl ?? "", r.platform, r.sessions, r.keyEvents]);

  function downloadCsv() {
    const csv = toCsv(exportHeaders, exportRows());
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${exportFilenameBase}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function downloadXlsxFile() {
    await downloadXlsx({
      fileName: `${exportFilenameBase}.xlsx`,
      sheets: [
        {
          sheetName: "Landing pages",
          columns: ["Landing page", "Page URL", "Top platform", "Sessions", "Conversions"],
          rows: exportRows(),
        },
      ],
    });
  }

  const visibleFrom = totalRows === 0 ? 0 : sliceStart + 1;
  const visibleTo = Math.min(totalRows, sliceEnd);

  const filterActive = platformFilter !== "all" || search.trim().length > 0;
  const totalCapped = cappedRows.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-neutral-500">
          {filterActive
            ? `${totalRows.toLocaleString()} of ${totalCapped.toLocaleString()} pages match · ${periodLabel}`
            : `Top ${totalCapped.toLocaleString()} pages · ${periodLabel}`}
        </p>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={downloadXlsxFile} className="text-purple-700">
            <i className="fa-regular fa-file-excel text-sm" aria-hidden="true" />
            <span>Excel</span>
            <i className="fa-regular fa-arrow-down-to-line text-xs" aria-hidden="true" />
          </Button>
          <Button variant="ghost" size="sm" onClick={downloadCsv} className="text-neutral-600">
            <i className="fa-regular fa-file-csv text-sm" aria-hidden="true" />
            <span>CSV</span>
            <i className="fa-regular fa-arrow-down-to-line text-xs" aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[16rem]">
          <span className="sr-only">Search landing pages</span>
          <i
            className="fa-regular fa-magnifying-glass pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs text-neutral-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(0);
            }}
            placeholder="Search pages…"
            className="w-full rounded-md bg-white py-2 pr-3 pl-9 text-sm ring-1 ring-neutral-950/10 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </label>
        <label className="inline-grid grid-cols-[1fr_--spacing(8)] items-center">
          <span className="sr-only">Filter by platform</span>
          <select
            value={platformFilter}
            onChange={(e) => {
              setPlatformFilter(e.target.value as PlatformFilter);
              setCurrentPage(0);
            }}
            className="col-span-full row-start-1 appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-sm ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All platforms</option>
            {availablePlatforms.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <svg
            viewBox="0 0 8 5"
            width="8"
            height="5"
            fill="none"
            aria-hidden="true"
            className="pointer-events-none col-start-2 row-start-1 place-self-center"
          >
            <path d="M.5.5 4 4 7.5.5" stroke="currentcolor" />
          </svg>
        </label>
        {filterActive ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setPlatformFilter("all");
              setCurrentPage(0);
            }}
            className="text-neutral-600"
          >
            Clear
          </Button>
        ) : null}
      </div>
      <DataTable<ReportLandingPageRow>
        key={`${sortColumn}-${sortDirection ?? "none"}`}
        columns={columns}
        data={pagedRows}
        getRowId={(r) =>
          // Two rows can share the same (landingPage, platform) pair when
          // they live on different hosts — e.g. `/` on app.skedsocial.com
          // vs skedsocial.com both show as `/` + LinkedIn. pageUrl carries
          // the full origin so it's the right disambiguator; a row without
          // a pageUrl gets the session count as a tiebreaker so React
          // still sees a unique key.
          `${r.landingPage}::${r.platform}::${r.pageUrl ?? `s${r.sessions}`}`
        }
        defaultSortColumn={sortColumn}
        defaultSortDirection={sortDirection}
        onSortChange={(col, dir) => {
          setSortColumn(col);
          setSortDirection(dir);
          setCurrentPage(0);
        }}
        sortComparator={comparator}
        emptyState={filterActive ? "No pages match your search or filter." : "No data available"}
      />
      <PaginationFooter
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(0);
        }}
        currentPage={safePage}
        pageCount={pageCount}
        onPageChange={setCurrentPage}
        visibleFrom={visibleFrom}
        visibleTo={visibleTo}
        totalRows={totalRows}
      />
    </div>
  );
}

function LandingPageCell({ row }: { row: ReportLandingPageRow }) {
  const label = (
    <span className="block max-w-[28rem] truncate font-mono text-sm text-neutral-900">
      {row.landingPage}
    </span>
  );
  if (!row.pageUrl) return label;
  return (
    <span className="inline-flex items-center gap-2">
      {label}
      <a
        href={row.pageUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open ${row.landingPage} in a new tab`}
        aria-label={`Open ${row.landingPage} in a new tab`}
        className="shrink-0 text-neutral-400 hover:text-purple-700 focus-visible:text-purple-700 focus-visible:outline-none"
      >
        <i className="fa-regular fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
      </a>
    </span>
  );
}

interface PaginationFooterProps {
  pageSize: PageSize;
  onPageSizeChange: (size: PageSize) => void;
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  visibleFrom: number;
  visibleTo: number;
  totalRows: number;
}

function PaginationFooter({
  pageSize,
  onPageSizeChange,
  currentPage,
  pageCount,
  onPageChange,
  visibleFrom,
  visibleTo,
  totalRows,
}: PaginationFooterProps) {
  const singlePage = pageCount <= 1;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-600">
      <label className="flex items-center gap-2">
        <span className="text-neutral-500">Rows per page</span>
        <select
          value={pageSize === "all" ? "all" : String(pageSize)}
          onChange={(e) => {
            const v = e.target.value;
            onPageSizeChange(v === "all" ? "all" : (Number(v) as PageSize));
          }}
          className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-medium text-neutral-900 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
        >
          {PAGE_SIZES.map((size) => (
            <option key={String(size)} value={String(size)}>
              {size === "all" ? "All" : size}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-3">
        <span className="tabular-nums text-neutral-500">
          {visibleFrom.toLocaleString()} to {visibleTo.toLocaleString()} of{" "}
          {totalRows.toLocaleString()}
        </span>
        <nav className="flex items-center gap-1" aria-label="Pagination">
          <PagerButton
            onClick={() => onPageChange(Math.max(0, currentPage - 1))}
            disabled={singlePage || currentPage === 0}
            aria-label="Previous page"
          >
            <i className="fa-regular fa-chevron-left text-xs" aria-hidden="true" />
          </PagerButton>
          <span className="tabular-nums px-2 text-neutral-700 max-sm:hidden">
            Page {currentPage + 1} of {pageCount}
          </span>
          <PagerButton
            onClick={() => onPageChange(Math.min(pageCount - 1, currentPage + 1))}
            disabled={singlePage || currentPage >= pageCount - 1}
            aria-label="Next page"
          >
            <i className="fa-regular fa-chevron-right text-xs" aria-hidden="true" />
          </PagerButton>
        </nav>
      </div>
    </div>
  );
}

function PagerButton({
  children,
  onClick,
  disabled,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex size-7 items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-600 transition-colors hover:border-neutral-300 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-200 disabled:hover:text-neutral-600"
      {...rest}
    >
      {children}
    </button>
  );
}

function comparator(
  a: ReportLandingPageRow,
  b: ReportLandingPageRow,
  column: string,
  direction: SortDirection,
): number {
  if (!direction) return 0;
  const dir = direction === "asc" ? 1 : -1;
  switch (column) {
    case "landingPage":
      return a.landingPage.localeCompare(b.landingPage) * dir;
    case "platform":
      return a.platform.localeCompare(b.platform) * dir;
    case "sessions":
      return (a.sessions - b.sessions) * dir;
    case "keyEvents":
      return (a.keyEvents - b.keyEvents) * dir;
    default:
      return 0;
  }
}
